import React, { useState, useContext, useEffect } from "react";
import MyContext from "./MyContext";
import VerticalButton from "./Vertical_button";
import Toolbar from "./Toolbar";
import {
  Layout,
  Button,
  Dropdown,
  Menu,
  Checkbox,
  Avatar,
  Typography,
  Modal,
  Radio,
  Upload,
  message,
} from "antd";
import {
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  UploadOutlined,
  FileExcelOutlined,
  FileOutlined,
  FolderOpenOutlined,
  SaveOutlined,
  PrinterOutlined,
  SolutionOutlined,
  CopyOutlined,
  SnippetsOutlined,
  CompressOutlined,
  ExpandOutlined,
  FullscreenOutlined,
  SnippetsFilled,
  CopyFilled,
  LineChartOutlined,
  PieChartOutlined,
  DatabaseOutlined,
  TableOutlined,
  FundOutlined,
  InsertRowBelowOutlined,
  InsertRowRightOutlined,
  BlockOutlined,
  CalculatorOutlined,
  CommentOutlined,
  ScissorOutlined,
  CalendarOutlined,
  QuestionCircleFilled,
  GlobalOutlined,
  CaretLeftFilled,
  CaretRightFilled,
  QuestionCircleOutlined,
  FileTextOutlined,
  BookOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  MailOutlined,
  CloudDownloadOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import * as XLSX from "xlsx";
import axios from "axios";

const { Header } = Layout;

function AppHeader({ onSidebarToggle, onShowContents }) {
  const {
    selectedKey,
    setSelectedKey,
    setIsModalVisible,
    setSelectedFile,
    selectedModule,
    setSelectedModule,
    excelFile,
    setExcelFile,
  } = useContext(MyContext);

  const [isSidebarChecked, setIsSidebarChecked] = useState(true);
  const [isToolbarChecked, setIsToolbarChecked] = useState(true);
  const [isMyLabChecked, setIsMyLabChecked] = useState(true);
  const [isFormatToolbarChecked, setIsFormatToolbarChecked] = useState(true);
  const [userInfo, setUserInfo] = useState({ username: "", role: "" });
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const [newDropdownPosition, setNewDropdownPosition] = useState({
    x: 0,
    y: 0,
  });

  // New states for Excel file upload modal
  const [isExcelModalVisible, setIsExcelModalVisible] = useState(false);
  const [selectedExcelModule, setSelectedExcelModule] = useState(null);
  const [selectedExcelSubmodule, setSelectedExcelSubmodule] = useState(null);

  // Module-Submodule mapping (unchanged)
  const MODULE_SUBMODULES = {
    "break-even": [
      { key: "1", name: "Crossover/ Cost-Volume Analysis" },
      { key: "2", name: "Breakeven Analysis (Cost vs. Revenues)" },
      { key: "3", name: "Multiproduct Breakeven Analysis" },
    ],
    forecasting: [
      { key: "4", name: "Time Series Analysis" },
      { key: "5", name: "Least Squares Regression" },
      { key: "6", name: "Regression Projector" },
      { key: "12", name: "Error Analysis" },
    ],
    inventory: [
      { key: "8", name: "Economic Order Quantity (EOQ) Model" },
      { key: "13", name: "Economic Production Lot Size Model" },
      { key: "23", name: "Quantity Discount(EOQ) Model" },
      { key: "9", name: "ABC Analysis" },
      {
        key: "19",
        name: "Reorder Point / Safety Stock (Discrete Distribution)",
      },
      { key: "20", name: "Reorder Point / Safety Stock (Normal Distribution)" },
      { key: "10", name: "Kanban Computation" },
      { key: "17", name: "Single Period Inventory (Discrete Distribution)" },
      { key: "18", name: "Single Period Inventory (Normal Distribution)" },
    ],
    "decision-making": [
      { key: "21", name: "Decision Table" },
      { key: "22", name: "Decision Trees" },
      { key: "11", name: "Create Preference Matrix" },
    ],
    "output-rates": [
      { key: "14", name: "Time Study" },
      { key: "15", name: "Sample Size for Time Studies" },
      { key: "16", name: "Sample Size for Work Sampling" },
    ],
  };

  // Main modules definition (unchanged)
  const MAIN_MODULES = {
    "break-even": { name: "Break Even Analysis", icon: LineChartOutlined },
    forecasting: { name: "Forecasting", icon: PieChartOutlined },
    inventory: { name: "Inventory Management", icon: DatabaseOutlined },
    "decision-making": { name: "Decision Making", icon: TableOutlined },
    "output-rates": { name: "Measuring Output Rates", icon: FundOutlined },
  };

  // Helper functions (unchanged)
  const getCurrentModule = () => {
    if (
      selectedModule &&
      [
        "break-even",
        "forecasting",
        "inventory",
        "decision-making",
        "output-rates",
      ].includes(selectedModule)
    ) {
      return selectedModule;
    }
    for (const [moduleKey, submodules] of Object.entries(MODULE_SUBMODULES)) {
      if (submodules.some((sub) => sub.key === selectedKey)) {
        return moduleKey;
      }
    }
    return null;
  };

  const getCurrentSubmodules = () => {
    const currentModule = getCurrentModule();
    return currentModule ? MODULE_SUBMODULES[currentModule] || [] : [];
  };

  const getSubmodulesForModule = (moduleKey) => {
    return MODULE_SUBMODULES[moduleKey] || [];
  };

  const handleNewClick = (event, source = "toolbar") => {
    const currentModule = getCurrentModule();
    if (!currentModule) {
      message.error(
        "Please select a module first from the sidebar or Module menu."
      );
      return;
    }
    const rect = event.target.getBoundingClientRect();
    setNewDropdownPosition({
      x: rect.left,
      y: rect.bottom,
    });
    setShowNewDropdown(true);
  };

  const handleSubmoduleSelect = (submoduleKey) => {
    setSelectedKey(submoduleKey);
    setIsModalVisible(true);
    setShowNewDropdown(false);
  };

  // New handler for "Open Excel File" button
  const handleOpenExcelClick = () => {
    setIsExcelModalVisible(true);
    setSelectedExcelModule(null);
    setSelectedExcelSubmodule(null);
    setExcelFile(null);
  };

  // Handle main module selection in Excel modal
  const handleExcelModuleChange = (e) => {
    setSelectedExcelModule(e.target.value);
    setSelectedExcelSubmodule(null); // Reset submodule when main module changes
    setExcelFile(null); // Reset file selection
  };

  // Handle submodule selection in Excel modal
  const handleExcelSubmoduleChange = (e) => {
    setSelectedExcelSubmodule(e.target.value);
  };

  // Handle file selection in Upload component
  // const handleFileChange = ({ file }) => {
  //   console.log("File selected in handleFileChange:", file);
  //   setExcelFile(file);
  //   return false; // Prevent automatic upload
  // };

  const handleFileChange = (file) => {
    console.log("File selected in handleFileChange:", file);

    // Validate file type
    const isExcel =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");

    if (!isExcel) {
      message.error("Please select a valid Excel file (.xlsx or .xls)");
      return false;
    }

    // Store the original file object directly
    // Don't modify the file object - keep the original File/Blob
    setExcelFile(file);
    return false; // Prevent automatic upload
  };

  // const handleExcelModalOk = async () => {
  //   if (!selectedExcelModule) {
  //     message.error("Please select a module.");
  //     return;
  //   }
  //   if (!selectedExcelSubmodule) {
  //     message.error("Please select a submodule.");
  //     return;
  //   }
  //   if (!excelFile) {
  //     message.error("Please upload an Excel file.");
  //     return;
  //   }

  //   console.log("Starting file parsing for:", excelFile.name);

  //   try {
  //     // Get the originFileObj if it exists, otherwise use the file directly
  //     const fileToRead = excelFile.originFileObj || excelFile;

  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       try {
  //         console.log("FileReader onload triggered");
  //         const data = new Uint8Array(e.target.result);
  //         const workbook = XLSX.read(data, { type: "array" });
  //         console.log("Workbook parsed, sheets:", workbook.SheetNames);

  //         const firstSheetName = workbook.SheetNames[0];
  //         if (!firstSheetName) {
  //           throw new Error("No sheets found in the Excel file");
  //         }

  //         const worksheet = workbook.Sheets[firstSheetName];
  //         const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  //         console.log("Excel File Content in JSON:", jsonData);

  //         message.success("Excel file parsed successfully!");

  //         // Close modal and reset states
  //         setIsExcelModalVisible(false);
  //         setSelectedExcelModule(null);
  //         setSelectedExcelSubmodule(null);

  //         // When the whole table is closed we need to set it to null
  //         // setExcelFile(null);

  //         // Set the selected key and open the main modal
  //         setSelectedKey(selectedExcelSubmodule);
  //         setExcelFile(excelFile)
  //         // setIsModalVisible(true);
  //       } catch (parseError) {
  //         console.error("Error parsing Excel content:", parseError);
  //         message.error(`Failed to parse Excel content: ${parseError.message}`);
  //       }
  //     };

  //     reader.onerror = (error) => {
  //       console.error("FileReader error:", error);
  //       message.error("Failed to read Excel file.");
  //     };

  //     reader.readAsArrayBuffer(fileToRead);
  //   } catch (error) {
  //     console.error("Error in handleExcelModalOk:", error);
  //     message.error(`Failed to process Excel file: ${error.message}`);
  //   }
  // };

  // Handle modal Cancel button

 const handleExcelModalOk = async () => {
  if (!selectedExcelModule) {
    message.error("Please select a module.");
    return;
  }
  if (!selectedExcelSubmodule) {
    message.error("Please select a submodule.");
    return;
  }
  if (!excelFile) {
    message.error("Please upload an Excel file.");
    return;
  }

  console.log("Starting file upload for:", excelFile.name);

  try {
    const fileToRead = excelFile.originFileObj || excelFile;
    const response = await sendToBackendAPI(
      fileToRead,
      selectedExcelModule,
      selectedExcelSubmodule
    );

    // Set selectedFile based on API response
    setSelectedFile({
      id: response.id,
      name: response.name,
      type: getFileTypeFromSubmodule(selectedExcelSubmodule), // Map submodule to file type
      inputData: response.input_data, // Store input data directly
    });

    message.success("Excel file uploaded successfully!");

    // Close modal and reset states
    setIsExcelModalVisible(false);
    setSelectedExcelModule(null);
    setSelectedExcelSubmodule(null);
    setExcelFile(null);
    setSelectedKey(selectedExcelSubmodule);
  } catch (error) {
    console.error("Error in handleExcelModalOk:", error);
    message.error(`Failed to upload Excel file: ${error.message}`);
  }
};

// Helper function to map submodule key to file type
const getFileTypeFromSubmodule = (submoduleKey) => {
  const submoduleToType = {
    "1": "crossover",
    "2": "direct",
    "3": "multiproduct",
    "4": "timeseries",
    "5": "leastsquares",
    "6": "regressionprojector",
    "8": "EOQ",
    "9": "abc",
    "10": "kanban",
    "11": "config",
    "12": "erroranalysis",
    "13": "productionlotsize",
    "14": "timestudy",
    "15": "samplesize_timestudy",
    "16": "samplesize_worksampling",
    "17": "singleperiod_discrete",
    "18": "singleperiod_normal",
    "19": "reorderpoint_discrete",
    "20": "reorderpoint_normaldist",
    "21": "decision_table",
    "22": "decision_trees",
    "23": "quantity_discount_EOQ",
  };
  return submoduleToType[submoduleKey] || "unknown";
};

  // Helper function to send data to backend API
  const sendToBackendAPI = async (originalFile, module, subModule) => {
    try {
      const token = localStorage.getItem("access_token");

      // Create FormData object
      const formData = new FormData();
      // const moduleDisplayName = module.charAt(0).toUpperCase() + module.slice(1);
      // const subModuleDisplayName = MODULE_SUBMODULES[module]?.find(sub => sub.key === subModule)?.name;
      
      // Add only the required fields as shown in the image
      formData.append("file", originalFile);
      formData.append("module", module);
      formData.append("sub_module", subModule);

      // Debug: Log FormData contents
      console.log("=== FormData being sent to backend ===");
      console.log("File:", originalFile);
      console.log("File name:", originalFile.name);
      console.log("File type:", originalFile.type);
      console.log("File size:", originalFile.size);
      // console.log("Module:", module);
      // console.log("Sub_module:", subModule);

      formData.append("module", "Forecasting");
      formData.append("sub_module", "Regression Projector");

      // Log all FormData entries
      console.log("=== FormData entries ===");
      for (let [key, value] of formData.entries()) {
        if (key === "file") {
          console.log(`${key}:`, {
            name: value.name,
            type: value.type,
            size: value.size,
            lastModified: value.lastModified,
          });
        } else {
          console.log(`${key}:`, value);
        }
      }
      console.log("=====================================");

      const response = await axios.post("/api/upload_excel/", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error sending data to API:", error);

      if (error.response) {
        // Server responded with error status
        console.log("Server error response:", error.response.data);
        const errorMessage =
          error.response.data?.message ||
          error.response.data ||
          "Server error occurred";
        throw new Error(`API Error: ${errorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        throw new Error(
          "No response from server. Please check your connection."
        );
      } else {
        // Something else happened
        throw new Error(`Request failed: ${error.message}`);
      }
    }
  };

  const handleExcelModalCancel = () => {
    setIsExcelModalVisible(false);
    setSelectedExcelModule(null);
    setSelectedExcelSubmodule(null);
    setExcelFile(null);
  };

  // Fetch user profile (unchanged)
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get("/api/profile/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUserInfo(response.data);
        localStorage.setItem("username", response.data.username);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    fetchUserProfile();
  }, []);

  // Logout handler (unchanged)
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  const handleHelpMenuClick = (key) => {
    switch (key) {
      case "contents":
        if (onShowContents) {
          onShowContents();
        } else {
          console.warn("onShowContents prop is not provided by the parent.");
        }
        break;
      case "index":
        console.log("Opening help index");
        break;
      case "topic-help":
        console.log("Opening topic help");
        break;
      case "tutorials":
        console.log("Opening tutorials");
        break;
      case "word-manual":
        window.open("/manuals/word-manual.doc", "_blank");
        break;
      case "pdf-manual":
        window.open("/manuals/pdf-manual.pdf", "_blank");
        break;
      case "email-support":
        window.location.href = "mailto:support@pomwindows.com";
        break;
      case "program-update":
        console.log("Checking for updates");
        break;
      case "user-information":
        console.log("Opening user information");
        break;
      case "about":
        console.log("Opening about POM for Windows");
        break;
      default:
        break;
    }
  };

  // User Profile dropdown menu (unchanged)
  const userMenu = (
    <Menu>
      <Menu.Item key="logout" onClick={handleLogout}>
        <LogoutOutlined /> Logout
      </Menu.Item>
    </Menu>
  );

  // Handle sidebar toggle (unchanged)
  const handleSidebarCheckboxChange = (e) => {
    setIsSidebarChecked(e.target.checked);
    onSidebarToggle(e.target.checked);
  };

  // Handle main module selection (unchanged)
  const handleMainModuleSelection = (moduleKey) => {
    console.log("Main module selected from header:", moduleKey);
    setSelectedModule(moduleKey);
    setSelectedKey(moduleKey);
    console.log("Module name:", MAIN_MODULES[moduleKey]?.name);
  };

  // Handle module selection (unchanged)
  const handleModuleSelection = (key) => {
    if (
      [
        "break-even",
        "forecasting",
        "inventory",
        "decision-making",
        "output-rates",
      ].includes(key)
    ) {
      handleMainModuleSelection(key);
    } else {
      setSelectedKey(key);
      setIsModalVisible(true);
    }
  };

  const HeadingStyle = {
    height: "auto",
    display: "flex",
    flexWrap: "wrap",
    borderBottom: "1px solid #fff",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "2px 10px",
    margin: "5px 0",
  };

  const subHeadingStyle = {
    display: "flex",
    borderRight: "1px solid #fff",
    maxHeight: "60px",
  };

  // File menu items (unchanged)
  const fileItems = (
    <Menu>
      <Menu.SubMenu title="New" key="new-submenu">
        {getCurrentSubmodules().length > 0 ? (
          getCurrentSubmodules().map((submodule) => (
            <Menu.Item
              key={submodule.key}
              onClick={() => handleSubmoduleSelect(submodule.key)}
            >
              {submodule.name}
            </Menu.Item>
          ))
        ) : (
          <Menu.Item
            key="no-module"
            onClick={() =>
              message.error(
                "Please select a module first from the sidebar or Module menu."
              )
            }
          >
            No module selected
          </Menu.Item>
        )}
      </Menu.SubMenu>
      <Menu.Item key="close">Close</Menu.Item>
      <Menu.Item key="save">Save</Menu.Item>
      <Menu.Item key="save-as">Save As...</Menu.Item>
      <Menu.Item key="save-as-excel">Save As Excel</Menu.Item>
      <Menu.Item key="print">Print</Menu.Item>
      <Menu.Item key="exit">Exit</Menu.Item>
    </Menu>
  );

  // Edit menu items (unchanged)
  const editItems = (
    <Menu>
      <Menu.Item key="insert row">Insert Row(s)</Menu.Item>
      <Menu.Item key="insert column">Insert Column(s)</Menu.Item>
      <Menu.Item key="delete row">Delete Row(s)</Menu.Item>
      <Menu.Item key="delete column">Delete Column(s)</Menu.Item>
      <Menu.Item key="copy">Copy</Menu.Item>
      <Menu.Item key="copy table with header">
        Copy Table with Headers
      </Menu.Item>
      <Menu.Item key="paste">Paste</Menu.Item>
      <Menu.Item key="copy down">Copy Down</Menu.Item>
    </Menu>
  );

  // View menu items (unchanged)
  const viewItems = (
    <Menu>
      <Menu.Item key="module tree">
        <Checkbox
          onChange={handleSidebarCheckboxChange}
          checked={isSidebarChecked}
        >
          Module Tree
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="mylab toolbar and menu">
        <Checkbox
          onChange={() => setIsMyLabChecked(!isMyLabChecked)}
          checked={isMyLabChecked}
        >
          Mylab Toolbar
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="toolbar">
        <Checkbox
          onChange={() => setIsToolbarChecked(!isToolbarChecked)}
          checked={isToolbarChecked}
        >
          Toolbar
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="format toolbar">
        <Checkbox
          onChange={() => setIsFormatToolbarChecked(!isFormatToolbarChecked)}
          checked={isFormatToolbarChecked}
        >
          Format Toolbar
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="full screen">Full Screen</Menu.Item>
    </Menu>
  );

  // Module dropdown items (unchanged)
  const moduleItems = (
    <Menu>
      {Object.entries(MAIN_MODULES).map(([key, module]) => {
        const IconComponent = module.icon;
        return (
          <Menu.Item
            key={key}
            onClick={() => handleMainModuleSelection(key)}
            icon={<IconComponent />}
          >
            {module.name}
          </Menu.Item>
        );
      })}
    </Menu>
  );

const helpItems = (
    <Menu onClick={({ key }) => handleHelpMenuClick(key)}>
      <Menu.Item key="contents" icon={<BookOutlined />}>
        Contents
      </Menu.Item>
      <Menu.Item key="index" icon={<FileTextOutlined />}>
        Index
      </Menu.Item>
      <Menu.Item key="topic-help" icon={<QuestionCircleOutlined />}>
        Topic Help
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="tutorials" icon={<VideoCameraOutlined />}>
        Tutorials
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="word-manual" icon={<FileTextOutlined />}>
        Word Manual
      </Menu.Item>
      <Menu.Item key="pdf-manual" icon={<FilePdfOutlined />}>
        PDF Manual
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="email-support" icon={<MailOutlined />}>
        E-mail Support
      </Menu.Item>
      <Menu.Item key="program-update" icon={<CloudDownloadOutlined />}>
        Program Update
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="user-information" icon={<SettingOutlined />}>
        User Information (Options)
      </Menu.Item>
      <Menu.Item key="about" icon={<InfoCircleOutlined />}>
        About POM for Windows
      </Menu.Item>
    </Menu>
  );


  return (
    <>
      <Header
        style={{ background: "#bcd2e8", padding: "0 10px", height: "auto" }}
      >
        <div style={HeadingStyle}>
          <div style={{ display: "flex" }}>
            <Dropdown overlay={fileItems} trigger={["click"]}>
              <Button type="text" style={{ marginRight: 8 }}>
                File
              </Button>
            </Dropdown>
            <Dropdown overlay={editItems} trigger={["click"]}>
              <Button type="text" style={{ marginRight: 8 }} disabled>
                Edit
              </Button>
            </Dropdown>
            <Dropdown overlay={viewItems} trigger={["click"]}>
              <Button type="text" style={{ marginRight: 8 }}>
                View
              </Button>
            </Dropdown>
            <Dropdown overlay={moduleItems} trigger={["click"]}>
              <Button type="text" style={{ marginRight: 8 }}>
                Module
              </Button>
            </Dropdown>
            <Button type="text" style={{ marginRight: 8 }} disabled>
              My Lab
            </Button>
            <Button type="text" style={{ marginRight: 8 }} disabled>
              Format
            </Button>
            <Button type="text" style={{ marginRight: 8 }} disabled>
              Tools
            </Button>
            <Dropdown overlay={helpItems} trigger={["click"]}>
              <Button type="text" style={{ marginRight: 8 }}>
                Help
              </Button>
            </Dropdown>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            <Avatar
              icon={<UserOutlined />}
              style={{ marginRight: 8, backgroundColor: "#87d068" }}
            />
            <Dropdown overlay={userMenu} trigger={["click"]}>
              <Button
                type="text"
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {userInfo.username
                  ? `Welcome, ${userInfo.username}`
                  : "Welcome"}{" "}
                <DownOutlined style={{ marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </div>
        </div>

        {isToolbarChecked && (
          <div style={HeadingStyle}>
            <div style={subHeadingStyle}>
              <VerticalButton
                icon={FileOutlined}
                text="New"
                onClick={(e) => handleNewClick(e, "toolbar")}
              />
              <VerticalButton icon={FolderOpenOutlined} text="Open" />
              <VerticalButton icon={SaveOutlined} text="Save" />
              <VerticalButton icon={PrinterOutlined} text="Print" />
            </div>
            <div style={subHeadingStyle}>
              <VerticalButton icon={SolutionOutlined} text="Solve" disabled />
            </div>
            <div style={subHeadingStyle}>
              <VerticalButton icon={CopyOutlined} text="Copy" disabled />
              <VerticalButton icon={SnippetsOutlined} text="Paste" disabled />
            </div>
            <div style={subHeadingStyle}>
              <VerticalButton
                icon={CompressOutlined}
                text="Autofit Columns"
                disabled
              />
              <VerticalButton
                icon={ExpandOutlined}
                text="Widen Columns"
                disabled
              />
              <VerticalButton
                icon={FullscreenOutlined}
                text="Full Screen"
                disabled
              />
            </div>
            <div style={subHeadingStyle}>
              <VerticalButton
                icon={SnippetsFilled}
                text="Paste From"
                disabled
              />
              <VerticalButton icon={CopyFilled} text="Copy Cell" disabled />
            </div>
            <div style={subHeadingStyle}>
              <VerticalButton
                icon={UploadOutlined}
                text="Upload Excel File"
                onClick={handleOpenExcelClick}
              />
              <VerticalButton icon={FileExcelOutlined} text="Open Excel File" />
            </div>
          </div>
        )}

        {isFormatToolbarChecked && (
          <div style={HeadingStyle}>
            <Toolbar />
          </div>
        )}
      </Header>

      {/* Custom New Dropdown (unchanged) */}
      {showNewDropdown && (
        <div
          style={{
            position: "fixed",
            top: newDropdownPosition.y,
            left: newDropdownPosition.x,
            background: "white",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 1000,
            minWidth: "200px",
          }}
        >
          <div style={{ padding: "8px 0" }}>
            {getCurrentSubmodules().map((submodule) => (
              <div
                key={submodule.key}
                onClick={() => handleSubmoduleSelect(submodule.key)}
                style={{
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#f5f5f5")}
                onMouseLeave={(e) => (e.target.style.background = "white")}
              >
                {submodule.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close dropdowns (unchanged) */}
      {showNewDropdown && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => {
            setShowNewDropdown(false);
          }}
        />
      )}

      {/* New Excel Upload Modal */}
      <Modal
        title="Open Excel File"
        visible={isExcelModalVisible}
        onOk={handleExcelModalOk}
        onCancel={handleExcelModalCancel}
        // okText="Upload"
        okText="Parse"
        cancelText="Cancel"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={5}>Select Module</Typography.Title>
          <Radio.Group
            onChange={handleExcelModuleChange}
            value={selectedExcelModule}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {Object.entries(MAIN_MODULES).map(([key, module]) => (
              <Radio key={key} value={key}>
                {module.name}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        {selectedExcelModule && (
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>Select Submodule</Typography.Title>
            <Radio.Group
              onChange={handleExcelSubmoduleChange}
              value={selectedExcelSubmodule}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {getSubmodulesForModule(selectedExcelModule).map((submodule) => (
                <Radio key={submodule.key} value={submodule.key}>
                  {submodule.name}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={5}>Upload Excel File</Typography.Title>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleFileChange}
            fileList={
              excelFile
                ? [
                    {
                      uid: excelFile.uid || Date.now().toString(),
                      name: excelFile.name,
                      status: "done",
                      originFileObj: excelFile,
                    },
                  ]
                : []
            }
            maxCount={1}
            showUploadList={true}
            onRemove={() => {
              setExcelFile(null);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>Select Excel File</Button>
          </Upload>
        </div>
      </Modal>
    </>
  );
}

export default AppHeader;
